/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  QUIZ_STEPS,
  TOTAL_QUIZ_STEPS,
  getQuizStepNumber,
  useFreeTrialStore,
} from 'components/FreeTrialFTUX/hooks/useFreeTrialStore';
import { submitEnrichment } from 'components/FreeTrialFTUX/utils/submitEnrichment';

import { analyticsClient } from 'utils/analytics/client';

import type { FreeTrialStepId, SignUpIdentity, UseCaseAnswer } from 'components/FreeTrialFTUX/config/types';

interface UseFreeTrialStateArgs {
  enrichmentFormId: string;
  thankYouUrl: string;
}

// Marketo's form.submit() fires the POST asynchronously after we call it.
// We leave the page on completion, so give that request a moment to leave
// the browser before navigating away, otherwise it can be cancelled.
const REDIRECT_DELAY_MS = 600;

const trackEvent = (event: string, payload: Record<string, any> = {}) => {
  if (typeof window === 'undefined') {
    return;
  }
  analyticsClient.track(event, payload);
};

export const useFreeTrialState = ({ enrichmentFormId, thankYouUrl }: UseFreeTrialStateArgs) => {
  const store = useFreeTrialStore();
  const [isHydrating, setIsHydrating] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasInitializedRef = useRef(false);
  const lastViewedStepRef = useRef<FreeTrialStepId | null>(null);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    const finishInit = () => {
      hasInitializedRef.current = true;
      // If the user already finished the flow once, restart on the sign-up
      // step with a clean slate. Mid-flow refreshes keep their progress.
      if (useFreeTrialStore.getState().completed) {
        useFreeTrialStore.getState().reset();
      }
      setIsHydrating(false);
    };

    try {
      if (useFreeTrialStore.persist.hasHydrated()) {
        finishInit();

        return;
      }

      const unsub = useFreeTrialStore.persist.onFinishHydration(finishInit);

      return unsub;
    } catch {
      // Persist API unavailable (e.g. SSR build artifacts) - just initialize.
      finishInit();

      return;
    }
  }, []);

  useEffect(() => {
    if (isHydrating || lastViewedStepRef.current === store.currentStep) {
      return;
    }
    lastViewedStepRef.current = store.currentStep;
    const isQuiz = store.currentStep !== 'signUp';
    trackEvent('free_trial_step_viewed', {
      step: store.currentStep,
      step_number: isQuiz ? getQuizStepNumber(store.currentStep as Exclude<FreeTrialStepId, 'signUp'>) : 0,
    });
  }, [isHydrating, store.currentStep]);

  const transitionTo = useCallback((next: () => void, delay = 250) => {
    setIsTransitioning(true);
    setTimeout(() => {
      next();
      setTimeout(() => setIsTransitioning(false), 50);
    }, delay);
  }, []);

  const buildEnrichmentPayload = useCallback(
    (extra: Record<string, string>) => {
      const email = store.identity?.email || '';
      const reached = Math.max(store.highestQuizStepReached, 1);

      return {
        Email: email,
        FT_Onboarding_Step_Reached__c: String(reached),
        FT_Onboarding_Use_Case_Skipped__c: String(store.skips.useCase),
        FT_Onboarding_Integration_Skipped__c: String(store.skips.integration),
        FT_Onboarding_Video_Skipped__c: String(store.skips.video),
        FT_Onboarding_Completed__c: String(store.completed),
        ...extra,
      };
    },
    [store.identity?.email, store.highestQuizStepReached, store.skips, store.completed],
  );

  const handleSignUpSuccess = useCallback(
    (identity: SignUpIdentity) => {
      store.setIdentity(identity);
      trackEvent('free_trial_signup_submitted', {
        email_domain: identity.email.split('@')[1],
      });
      transitionTo(() => store.goToStep('useCase'));
    },
    [store, transitionTo],
  );

  const completeAndRedirect = useCallback(async () => {
    store.markCompleted();

    const finalPayload = {
      Email: store.identity?.email || '',
      FT_Onboarding_Step_Reached__c: String(TOTAL_QUIZ_STEPS),
      FT_Onboarding_Use_Case_Skipped__c: String(store.skips.useCase),
      FT_Onboarding_Integration_Skipped__c: String(store.skips.integration),
      FT_Onboarding_Video_Skipped__c: String(store.skips.video),
      FT_Onboarding_Completed__c: 'true',
      ...(store.answers.useCase ? { FT_Primary_Use_Case__c: store.answers.useCase } : {}),
      ...(store.answers.integration ? { FT_First_Integration__c: store.answers.integration } : {}),
      ...(store.answers.watchedVideo ? { FT_Watched_Orientation_Video__c: 'true' } : {}),
    };

    // Show the loader (not a blank faded card) while the final enrichment posts,
    // then hand off to the standalone thank-you page (distinct URL is required
    // for conversion tracking).
    setIsRedirecting(true);
    await submitEnrichment(enrichmentFormId, finalPayload);
    setTimeout(() => {
      window.location.href = thankYouUrl;
    }, REDIRECT_DELAY_MS);
  }, [store, enrichmentFormId, thankYouUrl]);

  const advanceFromQuizStep = useCallback(
    (
      currentQuizStep: Exclude<FreeTrialStepId, 'signUp'>,
      extraEnrichment: Record<string, string>,
      meta: { skipped: boolean },
    ) => {
      const payload = buildEnrichmentPayload(extraEnrichment);
      submitEnrichment(enrichmentFormId, payload);

      trackEvent(meta.skipped ? 'free_trial_step_skipped' : 'free_trial_step_answered', {
        step: currentQuizStep,
        step_number: getQuizStepNumber(currentQuizStep),
      });

      const idx = QUIZ_STEPS.indexOf(currentQuizStep);
      const isLast = idx === QUIZ_STEPS.length - 1;
      if (isLast) {
        completeAndRedirect();

        return;
      }
      transitionTo(() => store.goToNext());
    },
    [buildEnrichmentPayload, enrichmentFormId, completeAndRedirect, store, transitionTo],
  );

  const handleUseCaseAnswer = useCallback(
    (answer: UseCaseAnswer) => {
      store.setUseCase(answer);
      advanceFromQuizStep('useCase', { FT_Primary_Use_Case__c: answer }, { skipped: false });
    },
    [store, advanceFromQuizStep],
  );

  const handleUseCaseSkip = useCallback(() => {
    store.markSkipped('useCase');
    advanceFromQuizStep('useCase', {}, { skipped: true });
  }, [store, advanceFromQuizStep]);

  const handleIntegrationSubmit = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        store.markSkipped('integration');
        advanceFromQuizStep('integration', {}, { skipped: true });

        return;
      }
      store.setIntegration(trimmed);
      advanceFromQuizStep('integration', { FT_First_Integration__c: trimmed }, { skipped: false });
    },
    [store, advanceFromQuizStep],
  );

  const handleIntegrationSkip = useCallback(() => {
    store.markSkipped('integration');
    advanceFromQuizStep('integration', {}, { skipped: true });
  }, [store, advanceFromQuizStep]);

  const handleVideoNext = useCallback(
    (watched: boolean) => {
      store.setWatchedVideo(watched);
      advanceFromQuizStep('video', watched ? { FT_Watched_Orientation_Video__c: 'true' } : {}, { skipped: false });
    },
    [store, advanceFromQuizStep],
  );

  const handleVideoSkip = useCallback(() => {
    store.markSkipped('video');
    advanceFromQuizStep('video', {}, { skipped: true });
  }, [store, advanceFromQuizStep]);

  return {
    currentStep: store.currentStep,
    identity: store.identity,
    answers: store.answers,
    skips: store.skips,
    isHydrating,
    isTransitioning,
    isRedirecting,
    handleSignUpSuccess,
    handleUseCaseAnswer,
    handleUseCaseSkip,
    handleIntegrationSubmit,
    handleIntegrationSkip,
    handleVideoNext,
    handleVideoSkip,
  };
};
